import { Link } from "react-router";
import {
  ExternalLink,
  Eye,
  FileText,
  Folder,
  Image as ImageIcon,
  Play,
} from "lucide-react";
import {
  ASSET_TYPE_LABEL,
  ASSET_VISIBILITY_LABEL,
  EMBED_PROVIDER_LABEL,
} from "@colab/shared";
import { cn, formatRelative } from "@/lib/utils";
import { FileThumbnail } from "@/components/shared/file-thumbnail";
import { AssetRowActions } from "./asset-row-actions";
import type { ContentAssetRow } from "./types";

// Phase 7 sub-bucket 13. Card render of a content_assets row — used by the
// list page's grid mode and the folder children render on the detail
// page. List-mode uses <AssetListRow> below. Visibility / archived /
// embed-provider chips mirror legacy AssetCard exactly.
export function AssetCard({ asset }: { asset: ContentAssetRow }) {
  const archived = !!asset.archived_at;

  const targetHref = asset.is_folder
    ? `/admin/content?folder=${asset.id}`
    : `/admin/content/${asset.id}`;

  return (
    <div
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border bg-card transition hover:border-foreground/15",
        archived && "opacity-60",
      )}
    >
      {asset.is_folder ? (
        <div className="flex items-start justify-between gap-2 px-5 pt-5">
          <Folder className="h-5 w-5 text-amber-500" />
          <AssetRowActions asset={asset} />
        </div>
      ) : (
        <div className="relative">
          <Link to={targetHref} className="block">
            <FileThumbnail
              className="rounded-none border-0 border-b"
              asset={{
                id: asset.id,
                fileUrl: asset.file_url,
                assetType: asset.asset_type,
                embedProvider: asset.embed_provider,
                externalUrl: asset.external_url,
              }}
            />
          </Link>
          <div className="absolute right-2 top-2 rounded-md bg-background/70 backdrop-blur-sm">
            <AssetRowActions asset={asset} />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col p-5 pt-3">
        <Link to={targetHref} className="block">
          <h3 className="line-clamp-2 font-display text-sm font-semibold tracking-tight hover:text-accent">
            {asset.title}
          </h3>
        </Link>

        {asset.description && (
          <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
            {asset.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
        {!asset.is_folder && (
          <span className="rounded-md border bg-secondary/50 px-1.5 py-0.5 font-medium uppercase tracking-wider text-muted-foreground">
            {ASSET_TYPE_LABEL[asset.asset_type]}
          </span>
        )}
        {asset.embed_provider && (
          <span className="rounded-md border bg-accent/5 px-1.5 py-0.5 font-medium text-accent">
            {EMBED_PROVIDER_LABEL[asset.embed_provider]}
          </span>
        )}
        <span className="rounded-md border bg-secondary/50 px-1.5 py-0.5 font-medium text-muted-foreground">
          {ASSET_VISIBILITY_LABEL[asset.visibility]}
        </span>
        {asset.categories.map((c) => (
          <span
            key={c}
            className="rounded-md border bg-muted/40 px-1.5 py-0.5 text-muted-foreground"
          >
            {c}
          </span>
        ))}
        {archived && (
          <span className="rounded-md border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5 font-medium uppercase tracking-wider text-amber-600">
            Archived
          </span>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between border-t pt-3 text-[11px] text-muted-foreground">
        {!asset.is_folder ? (
          <Link
            to={`/admin/content/${asset.id}?tab=views`}
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            <Eye className="h-3 w-3" />
            views
          </Link>
        ) : (
          <span />
        )}
        <span>Updated {formatRelative(asset.updated_at)}</span>
      </div>

      {!asset.is_folder && asset.external_url && (
        <a
          href={asset.external_url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border bg-background py-1.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          Open external
          <ExternalLink className="h-3 w-3" />
        </a>
        )}
      </div>
    </div>
  );
}

// List-mode row. Smaller footprint than the card so a page of folders +
// assets shows in a single scrollable table.
export function AssetListRow({ asset }: { asset: ContentAssetRow }) {
  const archived = !!asset.archived_at;
  const Icon = asset.is_folder
    ? Folder
    : asset.embed_provider
      ? Play
      : asset.file_url
        ? ImageIcon
        : FileText;
  const targetHref = asset.is_folder
    ? `/admin/content?folder=${asset.id}`
    : `/admin/content/${asset.id}`;

  return (
    <tr className={cn("group transition-colors hover:bg-muted/30", archived && "opacity-60")}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon
            className={cn(
              "h-4 w-4 shrink-0",
              asset.is_folder ? "text-amber-500" : "text-muted-foreground",
            )}
          />
          <Link
            to={targetHref}
            className="truncate font-medium text-foreground hover:text-accent"
          >
            {asset.title}
          </Link>
          {archived && (
            <span className="rounded-md border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-600">
              Archived
            </span>
          )}
        </div>
        {asset.description && (
          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {asset.description}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {asset.is_folder ? "Folder" : ASSET_TYPE_LABEL[asset.asset_type]}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {ASSET_VISIBILITY_LABEL[asset.visibility]}
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
        {formatRelative(asset.updated_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <AssetRowActions asset={asset} />
      </td>
    </tr>
  );
}
